"use client"
// @ts-nocheck — third-party hook return types not fully typed
import { Alert, AlertDescription } from "@repo/ui/components/ui/alert"
import { Badge } from "@repo/ui/components/ui/badge"
import { Button } from "@repo/ui/components/ui/button"
import { Checkbox } from "@repo/ui/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/ui/dialog"
import { Input } from "@repo/ui/components/ui/input"
import { Label } from "@repo/ui/components/ui/label"
import { ScrollArea } from "@repo/ui/components/ui/scroll-area"
import { useRequestDemo } from "@repo/ui/hooks/api/auth/use-demo"
import { useSignup } from "@repo/ui/hooks/api/auth/use-signup"
import { useCreateSignupCheckout } from "@repo/ui/hooks/api/payment/use-signup-checkout"
import { usePublicSubscriptionConfig } from "@repo/ui/hooks/api/settings"
import { useRouter } from "@repo/ui/hooks/use-router"
import { parseApiError } from "@repo/ui/lib/utils/api-error.util"
import { AlertCircle, Check, Eye, EyeOff, Loader2 } from "lucide-react"
import { useState } from "react"

// ─── Types ────────────────────────────────────────────────────────────────────

export type SelectedPlanType = "BASIC" | "ADVANCED"

export interface SelectedTier {
  id?: string
  name: string
  price: string | number
  period: string
  features?: string[]
}

interface SignupDialogProps {
  open: boolean
  isFreeMode: boolean
  selectedPlan: SelectedPlanType
  selectedTier: SelectedTier | null
  onClose: () => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

const E164_REGEX = /^\+[1-9]\d{1,14}$/

function validateOptionalPhone(value: string): boolean {
  const t = value.trim()
  return !t || E164_REGEX.test(t)
}

function getPasswordStrength(password: string): { strength: number; message: string } {
  let s = 0
  if (password.length >= 8) s++
  if (password.length >= 12) s++
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) s++
  if (/\d/.test(password)) s++
  if (/[^a-zA-Z\d]/.test(password)) s++
  const messages = ["Très faible", "Faible", "Moyen", "Fort", "Très fort"]
  return { strength: s, message: messages[Math.min(s, 4)] ?? "Faible" }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SignupDialog({
  open,
  isFreeMode,
  selectedPlan,
  selectedTier,
  onClose,
}: SignupDialogProps): JSX.Element {
  const router = useRouter()
  const { data: subscription } = usePublicSubscriptionConfig()
  const { mutateAsync: requestDemo, isPending: isDemoLoading } = useRequestDemo()
  const { mutateAsync: createSignupCheckout, isPending: isCheckoutLoading } =
    useCreateSignupCheckout()
  const { mutateAsync: signup, isPending: isSignupLoading } = useSignup()

  const requirePayment = subscription?.requirePaymentBeforeSignup ?? true
  const allowFreeTrial = subscription?.allowFreeTrial ?? false
  const isPending = isDemoLoading || isCheckoutLoading || isSignupLoading

  const [fullName, setFullName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [promoCode, setPromoCode] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  function clearFieldError(field: string) {
    if (fieldErrors[field]) setFieldErrors((p) => ({ ...p, [field]: "" }))
  }

  function handleClose() {
    onClose()
    setPhoneNumber("")
    setError(null)
    setFieldErrors({})
  }

  function validate(requireTier: boolean): Record<string, string> {
    const errors: Record<string, string> = {}
    if (!fullName || fullName.trim().length < 2)
      errors.fullName = "Le nom complet doit contenir au moins 2 caractères"
    if (!email) errors.email = "L'email est requis"
    else if (!validateEmail(email)) errors.email = "Format d'email invalide"
    if (!password) errors.password = "Le mot de passe est requis"
    else if (password.length < 8) errors.password = "Minimum 8 caractères"
    else if (getPasswordStrength(password).strength < 2)
      errors.password = `Mot de passe trop faible. Ajoutez des majuscules, chiffres ou caractères spéciaux`
    if (!confirmPassword) errors.confirmPassword = "Veuillez confirmer le mot de passe"
    else if (password !== confirmPassword) errors.confirmPassword = "Les mots de passe ne correspondent pas"
    if (!acceptTerms) errors.terms = "Vous devez accepter les CGV-CGU"
    if (requireTier && !selectedTier?.id) errors.tier = "Veuillez sélectionner un forfait"
    if (phoneNumber.trim() && !validateOptionalPhone(phoneNumber))
      errors.phoneNumber = "Format invalide. Utilisez le format E.164 (ex. +33612345678)"
    return errors
  }

  function applySignupError(err: unknown) {
    const parsed = parseApiError(err)
    if (parsed.field === "phone_number") {
      setFieldErrors((previous) => ({ ...previous, phoneNumber: parsed.message }))
    } else if (parsed.field === "email") {
      setFieldErrors((previous) => ({ ...previous, email: parsed.message }))
    }
    setError(parsed.message)
  }

  async function handleFreeSignup() {
    setError(null)
    setFieldErrors({})
    const errors = validate(false)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setError("Veuillez corriger les erreurs dans le formulaire")
      return
    }
    try {
      const nom = fullName.split(" ").slice(0, -1).join(" ") || fullName
      const prenom = fullName.split(" ").slice(-1).join(" ") || "User"
      await signup({
        email: email.trim().toLowerCase(),
        password,
        nom,
        prenom,
        forfait: "GRATUIT",
        phoneNumber: phoneNumber.trim() || undefined,
      } as any)
      handleClose()
      router.push("/auth/login?message=account_created")
    } catch (err: unknown) {
      applySignupError(err)
    }
  }

  async function handlePaidSignup() {
    setError(null)
    setFieldErrors({})
    const errors = validate(true)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setError("Veuillez corriger les erreurs dans le formulaire")
      return
    }
    if (!selectedTier?.id) {
      setError("Veuillez sélectionner un forfait")
      return
    }
    try {
      const nom = fullName.split(" ").slice(0, -1).join(" ") || fullName
      const prenom = fullName.split(" ").slice(-1).join(" ") || "User"
      if (requirePayment) {
        const { data } = await createSignupCheckout({
          email,
          nom,
          prenom,
          password,
          tierId: selectedTier.id,
          planType: selectedPlan,
          promoCode: promoCode || undefined,
          phoneNumber: phoneNumber.trim() || undefined,
        })
        window.location.href = data.checkoutUrl
      } else if (allowFreeTrial) {
        await requestDemo({ email, nom, prenom, planType: selectedPlan, password, tierId: selectedTier.id })
        handleClose()
        router.push("/auth/login?message=account_created")
      } else {
        setError("Les inscriptions sont temporairement désactivées")
      }
    } catch (err: unknown) {
      applySignupError(err)
    }
  }

  async function handleSubmit() {
    if (isFreeMode) await handleFreeSignup()
    else await handlePaidSignup()
  }

  const canSubmit =
    !!email &&
    !!password &&
    !!confirmPassword &&
    password === confirmPassword &&
    !!fullName &&
    acceptTerms &&
    password.length >= 8

  const pwStrength = password ? getPasswordStrength(password) : null

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
    >
      <DialogContent className="max-w-lg overflow-hidden flex flex-col max-h-[95vh]">
        <DialogHeader className="space-y-1 pb-3 border-b flex-shrink-0">
          <DialogTitle className="text-lg font-semibold text-foreground">
            Créer votre compte Dendromap
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {isFreeMode ? (
              <span className="text-primary font-semibold">
                Accès Gratuit · 1 mois d&apos;essai · Sans CB
              </span>
            ) : (
              <>
                {selectedPlan === "BASIC" ? "Professionnels" : "Advanced+"} &bull;{" "}
                {requirePayment ? (
                  <span className="text-amber-600 font-semibold">
                    Paiement requis avant activation
                  </span>
                ) : allowFreeTrial ? (
                  <span className="text-primary font-semibold">
                    Essai gratuit : {subscription?.freeTrialMaxTrees || 1} arbre
                  </span>
                ) : (
                  <span className="text-muted-foreground">Inscription</span>
                )}
              </>
            )}
          </p>
        </DialogHeader>

        <div className="space-y-4 py-3 overflow-y-auto flex-1 min-h-0 px-0.5">
          {/* Selected plan summary */}
          {!isFreeMode && selectedTier && (
            <div className="rounded-xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-card p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Badge className="bg-primary text-primary-foreground text-[10px] px-2 py-0.5 mb-1">
                    Forfait sélectionné
                  </Badge>
                  <h3 className="text-sm font-semibold text-foreground">{selectedTier.name}</h3>
                </div>
                <div className="text-right">
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-xl font-bold text-foreground">
                      {String(selectedTier.price).replace("€", "")}
                    </span>
                    <span className="text-base font-bold text-foreground">€</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground">{selectedTier.period}</div>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <div className="space-y-3">
            {/* Full name */}
            <div>
              <Label htmlFor="signup-fullName" className="text-xs font-medium mb-1 block">
                Nom complet <span className="text-destructive">*</span>
              </Label>
              <Input
                id="signup-fullName"
                value={fullName}
                onChange={(e) => { setFullName(e.target.value); clearFieldError("fullName") }}
                placeholder="Jean Dupont"
                className={`h-9 text-sm ${fieldErrors.fullName ? "border-destructive focus-visible:ring-destructive" : ""}`}
                autoComplete="name"
              />
              {fieldErrors.fullName && (
                <p className="text-[11px] text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="size-3" aria-hidden />
                  {fieldErrors.fullName}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="signup-email" className="text-xs font-medium mb-1 block">
                Adresse email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearFieldError("email") }}
                placeholder="jean.dupont@exemple.com"
                className={`h-9 text-sm ${fieldErrors.email ? "border-destructive focus-visible:ring-destructive" : ""}`}
                autoComplete="email"
              />
              {fieldErrors.email && (
                <p className="text-[11px] text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="size-3" aria-hidden />
                  {fieldErrors.email}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="signup-phone" className="text-xs font-medium mb-1 block">
                Téléphone
              </Label>
              <Input
                id="signup-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phoneNumber}
                onChange={(e) => { setPhoneNumber(e.target.value); clearFieldError("phoneNumber") }}
                placeholder="+33612345678"
                className={`h-9 text-sm ${fieldErrors.phoneNumber ? "border-destructive focus-visible:ring-destructive" : ""}`}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Format E.164 : commence par + et l&apos;indicatif pays
              </p>
              {fieldErrors.phoneNumber && (
                <p className="text-[11px] text-destructive mt-0.5 flex items-center gap-1">
                  <AlertCircle className="size-3" aria-hidden />
                  {fieldErrors.phoneNumber}
                </p>
              )}
            </div>

            {/* Password + Confirm side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="signup-password" className="text-xs font-medium mb-1 block">
                  Mot de passe <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="signup-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); clearFieldError("password") }}
                    placeholder="Min. 8 caractères"
                    className={`h-9 pr-9 text-sm ${fieldErrors.password ? "border-destructive focus-visible:ring-destructive" : ""}`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showPassword ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                  </button>
                </div>
                {password && !fieldErrors.password && pwStrength && (
                  <p
                    className={`text-[11px] mt-1 ${pwStrength.strength >= 3
                      ? "text-emerald-600"
                      : pwStrength.strength >= 2
                        ? "text-amber-600"
                        : "text-destructive"
                      }`}
                  >
                    Force : {pwStrength.message}
                  </p>
                )}
                {fieldErrors.password && (
                  <p className="text-[11px] text-destructive mt-1 flex items-center gap-1">
                    <AlertCircle className="size-3" aria-hidden />
                    {fieldErrors.password}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="signup-confirm" className="text-xs font-medium mb-1 block">
                  Confirmer <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="signup-confirm"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError("confirmPassword") }}
                    placeholder="Répétez"
                    className={`h-9 pr-9 text-sm ${fieldErrors.confirmPassword ? "border-destructive focus-visible:ring-destructive" : ""}`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showConfirmPassword ? "Masquer" : "Afficher"}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="size-3.5" />
                    ) : (
                      <Eye className="size-3.5" />
                    )}
                  </button>
                </div>
                {password && confirmPassword && password === confirmPassword && password.length >= 8 && !fieldErrors.confirmPassword && (
                  <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1">
                    <Check className="size-3" aria-hidden /> OK
                  </p>
                )}
                {fieldErrors.confirmPassword && (
                  <p className="text-[11px] text-destructive mt-1 flex items-center gap-1">
                    <AlertCircle className="size-3" aria-hidden />
                    {fieldErrors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            {/* Promo code (paid mode only) */}
            {!isFreeMode && (
              <div>
                <Label htmlFor="signup-promo" className="text-xs font-medium mb-1 block">
                  Code promo{" "}
                  <span className="text-muted-foreground font-normal">(optionnel)</span>
                </Label>
                <Input
                  id="signup-promo"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="SUMMER2025"
                  className="h-9 text-sm uppercase font-mono"
                />
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <Alert variant="destructive" className="py-2">
              <AlertCircle className="size-4" />
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}

          {/* Terms */}
          <div className="pt-2 border-t border-border">
            <div className="flex items-start gap-2.5">
              <Checkbox
                id="signup-terms"
                checked={acceptTerms}
                onCheckedChange={(checked: any) => setAcceptTerms(checked as boolean)}
                className="mt-0.5"
              />
              <Label
                htmlFor="signup-terms"
                className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
              >
                J&apos;accepte les{" "}
                <a
                  href="/politique-de-confidentialite"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium"
                >
                  CGV-CGU
                </a>{" "}
                de Dendromap®
              </Label>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-3 border-t border-border flex-shrink-0">
          <div className="flex gap-2 w-full">
            <Button variant="outline" onClick={handleClose} className="flex-1 h-9 text-sm">
              Annuler
            </Button>
            <Button
              disabled={isPending || !canSubmit}
              onClick={handleSubmit}
              className="flex-1 h-9 text-sm font-semibold disabled:opacity-50"
            >
              {isPending ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  {isFreeMode ? "Création…" : requirePayment ? "Redirection…" : "Création…"}
                </span>
              ) : isFreeMode ? (
                "Créer mon accès gratuit"
              ) : requirePayment ? (
                `Souscrire & Payer ${selectedTier?.price}${selectedTier?.period}`
              ) : allowFreeTrial ? (
                "Démarrer l'essai gratuit"
              ) : (
                "Créer mon compte"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
